import {
  useEffect,
  useMemo,
  useRef,
  Suspense,
  lazy,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Loading from "./components/Loading";
import { loginParams } from "./services/helpers/login-params";
import { setActiveBet } from "./store/entities/bet-slice";
import config from "./config/config";
import { setBetAmount } from "./store/entities/game-controls/bet-amount-slice";
import { setActiveFreeBet } from "./store/entities/free-bet-slice";
import { toggleFreeBetsState } from "./store/entities/free-bets-state-slice";
import { setFreeTicketInfo } from "./store/entities/freetickets/free-ticket-info-slice";
import { setBonusCumulative } from "./store/entities/freetickets/bonus-cumulative-slice";
import { checkValidUntilFreeTicket } from "./services/free-tickets";
import { UAParser } from "ua-parser-js";
import translate from "./services/translation/lang";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { makeDynamicUrlPreserveProtoPort } from "./services/helpers/get-base-domain";

// Lazy load components
const HandlePopupMessage = lazy(() =>
  import("./components/popups/HandlePopupMessage")
);
const GamePlay = lazy(() => import("./components/GamePlay"));
const GameControls = lazy(() => import("./components/GameControls"));

export default function Game() {
  const dispatch = useDispatch();
  const login = useSelector((state) => state.login);
  const gameDivRef = useRef(null);
  const gamePlayRef = useRef(null);
  const musicLoaded = useSelector((state) => state.soundsLoaded);
  const wsStatus = useSelector((state) => state.webSocketStatus);
  const serverMessages = useSelector((state) => state.serverMessages);

  const [isPortrait, setIsPortrait] = useState(true);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

  useEffect(() => {
    const configuredServer = config?.default?.gameServers;
    let serverUrl = configuredServer;

    if (typeof window !== "undefined") {
      const host = window.location.hostname;

      // localhost / dev → ostaje config
      if (host !== "localhost" && host !== "127.0.0.1") {
        serverUrl = makeDynamicUrlPreserveProtoPort(configuredServer);
      }
    }

    dispatch({
      type: "WEBSOCKET_CONNECT",
      payload: {
        url: serverUrl,
      },
    });

    return () => {
      dispatch({ type: "WEBSOCKET_DISCONNECT" });
    };
  }, [dispatch]);

  useEffect(() => {
    if (wsStatus === "connected") {
      dispatch({
        type: "WEBSOCKET_SEND",
        payload: ["login", loginParams],
      });
    }
  }, [wsStatus, dispatch]);

  useEffect(() => {
    if (login) {
      // this below is that music player is not unused var
      // and we need import of music player to be at least imported
      // to set soundsLoaded to true
      import("./services/audio/music-player")
        .then((module) => {
          module.default.state("rr_trafficambience_loop");
        })
        .catch((err) => {
          console.error("Failed to load musicPlayer.js:", err);
        });

      if (login.ActiveTicket && login) {
        if (
          login?.ActiveTicket?.TicketType === 3 &&
          login.ActiveTicket?.TicketID
        ) {
          if (
            login?.CurrentFreeTicketsCount &&
            login?.CurrentFreeTicketsMoney
          ) {
            dispatch(
              setActiveFreeBet({
                Status: 1,
                TicketID: login.ActiveTicket?.TicketID,
              })
            );
            dispatch(
              setFreeTicketInfo({
                noOfRounds:
                  login.CurrentFreeTicketsCount + login?.FreeTicketsCount,
                value: login.ActiveTicket?.Bet,
                currency: login.ActiveTicket?.CurrencyID,
                freeTicketCount:
                  login.CurrentFreeTicketsCount + login?.FreeTicketsCount,
                validUntil: login?.FreeTicketsExpiration,
              })
            );
            // if (login?.FreeTicketsStarted) {
            //   dispatch(toggleFreeBetsState(true));
            // }
            dispatch(toggleFreeBetsState(true));
          }
        } else {
          dispatch(
            setActiveBet({
              Status: 1,
              TicketID: login.ActiveTicket.TicketID,
            })
          );
        }

        dispatch(setBetAmount(login.ActiveTicket.Bet));
      }

      if (
        login &&
        !login?.ActiveTicket &&
        login?.FreeTicketsCount &&
        login?.FreeTicketsMoney
      ) {
        dispatch(
          setFreeTicketInfo({
            noOfRounds: login.FreeTicketsCount,
            value: login.FreeTicketsMoney,
            currency: login.CurrencyID,
            freeTicketCount: login.FreeTicketsCount,
            validUntil: login?.FreeTicketsExpiration,
          })
        );
        if (login?.FreeTicketsStarted) {
          dispatch(toggleFreeBetsState(true));
        }
      }

      // sets culumative free tickets win
      if (login?.FreeTicketsTotalWin) {
        dispatch(setBonusCumulative(login.FreeTicketsTotalWin));
      }

      // checks for free tickets bonus modal validUntil timestamp
      checkValidUntilFreeTicket(login?.FreeTicketsExpiration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  const checkOrientation = () => {
    setIsPortrait(window.innerHeight > window.innerWidth);
  };

  useEffect(() => {
    const parser = new UAParser();
    const deviceType = parser.getDevice().type; // "mobile", "tablet", or undefined
    setIsMobileOrTablet(deviceType === "mobile" || deviceType === "tablet");

    checkOrientation();
    window.addEventListener("resize", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
    };
  }, []);

  useEffect(() => {
    if (isMobileOrTablet && !isPortrait) {
      document.body.classList.add("landscape");
    } else {
      document.body.classList.remove("landscape");
    }

    return () => {
      document.body.classList.remove("landscape");
    };
  }, [isMobileOrTablet, isPortrait]);

  return useMemo(
    () => (
      <div
        className={
          "game " + (isMobileOrTablet && !isPortrait ? "landscape" : "")
        }
        ref={gameDivRef}
      >
        {/* NEW: Popup handler je uvek mount-ovan, da moze da prikaze Disconnect/Login error i pre logina */}
        <Suspense fallback={null}>
          <HandlePopupMessage />
        </Suspense>

        {login && musicLoaded && wsStatus === "connected" ? (
          <Suspense fallback={<Loading />}>
            {isMobileOrTablet && !isPortrait ? (
              <div className="landscape-message">
                {translate("please_play_the_game_in_portait_mode")}
              </div>
            ) : null}
            <Header />
            <div className="gameplay-holder">
              <GamePlay ref={gamePlayRef} login={login} />
              <GameControls gameDivRef={gameDivRef} gamePlayRef={gamePlayRef} />
            </div>
            <Footer gameDivRef={gameDivRef} />
          </Suspense>
        ) : serverMessages ? (
          <Loading message={serverMessages} />
        ) : (
          <Loading message={""} />
        )}
      </div>
    ),
    [login, musicLoaded, isMobileOrTablet, isPortrait, wsStatus, serverMessages]
  );
}
